<template>
  <!-- Revit rendering with Canvas -->
  <SchemaRevit 
    v-if="isRevitProject && this.floorSchema"
    :floorSchema="floorSchema"
    :floorMapInfo="floorMapInfo"
    :activeFloorParamTypeIds="activeFloorParamTypeIds"
    :updateParamBody="updateParamBody"
    :updateData="updateData"
    :editingMode="editingMode"
    :getFloorSchemaData="getFloorSchemaData"
    :activeData="activeData"
    :bufferParams="bufferParams"
    :getBufferParamsData="getBufferParamsData"
    :getFloorLegendData="getFloorLegendData"
    :updateFloorSchema="updateFloorSchema"
    :schemaFetching="schemaFetching"
    :activeFloorFlatId="activeFloorFlatId"
    :flatBorders="flatBorders"
    :params="params"
    :floorMapId="floorMapId"
    :deletingMode="deletingMode"
    :activeFlat="activeFlat"
    :groupEditingMode="groupEditingMode"
  />
  
  <!-- AutoCAD rendering with Konva -->
  <div
    v-else-if="!isRevitProject && this.floorSchema"
    :style="{...this.wrapperStyle, ...this.wrapperStyles}"
  >
    <v-stage
      :config="{...stageConfig, ...stageView}"
      ref="stage"
      @wheel="onWheel"
    >
      <v-layer>
        <v-image
          v-if="backgroundImage"
          :config="{ image: this.backgroundImage, 
                      y: 0, 
                      opacity: 0.5,  
                   }"
        />
        <v-line
          v-if="this.bufferParams?.lines?.length"
          v-for="(wall, index) in this.bufferParams.lines"
          :key="wall.floor_buffer_id"
          style="cursor: pointer;"
          :config="{
            points: this.flattenPoints(wall.points),
            stroke: this.getLineColor(wall, `buffer-${index}`, wall.floor_flat_id), 
            strokeWidth: (this.activeFloorFlatId && !this.editingMode) ? wall?.floor_flat_id === this.activeFloorFlatId ? 8 : 0 : 8,
            x: 0,
            y: 0, 
            lineJoin: 'round',
        }"
          @click="handleLineClick(wall, $event)"
          @mouseenter="onMouseEnter(wall, $event, `buffer-${index}`)"
          @mouseleave="onMouseLeave"
        />
        <v-line
          v-for="(wall, index) in this.floorSchema?.lines"
          :key="wall.floor_param_id"
          style="cursor: pointer;"
          :config="{
            points: this.flattenPoints(wall.points),
            stroke: this.getLineColor(wall, `param-${index}`, wall.floor_flat_id), 
            strokeWidth: this.getLineWidth(wall),
            x: 0,
            y: 0, 
            lineJoin: 'round',
            dash: this.getLineDash(wall)
          }"
          @click="handleLineClick(wall, $event)"
          @mouseenter="onMouseEnter(wall, $event, `param-${index}`)"
          @mouseleave="onMouseLeave"
        />
        <v-circle
          v-if="!!this.floorSchema?.circles?.length"
          v-for="(circle, index) in this.floorSchema?.circles"
          :config="{
                    x: circle.points[0],
                    y: circle.points[1],
                    radius: 10,
                    opacity: '1',
                    stroke: this.getLineColor(circle, `circle-${index}`, circle.floor_flat_id),
                    strokeWidth: this.getLineWidth(circle),
                  }"
          @click="handleLineClick(circle, $event)"
          @mouseenter="onMouseEnter(circle, $event, `circle-${index}`)"
          @mouseleave="onMouseLeave"
        />
        <v-circle
          v-if="!!this.bufferParams?.circles?.length"
          v-for="(circle, index) in this.bufferParams?.circles"
          :config="{
                    x: circle.points[0],
                    y: circle.points[1],
                    radius: 10,
                    opacity: '1',
                    stroke: '#000',
                    strokeWidth: (this.activeFloorFlatId && !this.editingMode) ? wall?.floor_flat_id === this.activeFloorFlatId ? 8 : 0 : 8,
                  }"
          @click="handleLineClick(circle, $event)"
          @mouseenter="onMouseEnter(circle, $event, `circle-${index}`)"
          @mouseleave="onMouseLeave"
        />
        <v-circle
          v-if="!!this.floorSchema?.walls_center?.length && !this.editingMode"
          v-for="room in this.floorSchema?.walls_center"
          :config="this.getCircleSettings(room)"
        />
        <v-text
          v-if="!!this.floorSchema?.walls_center?.length && !this.editingMode"
          v-for="room in this.floorSchema?.walls_center"
          :key="'label_' + room.flat_num"
          :config="{
              text: `кв ${room.flat_num}`,
              x: room.center_x,
              y: room.center_y,
              fontSize: 40,
              fontStyle: 'bold',
              fontFamily: 'sans-serif',
              fill: '#333',
              align: 'center',
              listening: false
            }"
        />
        <v-text
          v-if="!!this.floorSchema?.area_center?.length"
          v-for="areaCenter in this.floorSchema?.area_center"
          :key="'area_' + areaCenter.room_id"
          :config="{
              text: `${areaCenter.area}`,
              x: areaCenter.center_x - String(areaCenter.area)?.length * 4,
              y: areaCenter.center_y - 12,
              fontSize: (this.activeFloorFlatId) ? text?.floor_flat_id === this.activeFloorFlatId ? 20 : 0 : 20,
              fontStyle: 'bold',
              fontFamily: 'sans-serif',
              fill: '#333',
              align: 'center',
              listening: false
            }"
        />
        <v-text
          v-if="!!this.floorSchema?.texts?.length"
          v-for="text in this.floorSchema?.texts"
          :key="'frame_' + text.floor_param_id + text.frame_name"
          :config="{
              text: text.frame_name,
              x: text.center_point[0] - text.frame_name.length * 4,
              y: text.center_point[1],
              fontSize: (this.activeFloorFlatId && !this.editingMode) ? text?.floor_flat_id === this.activeFloorFlatId ? 12 : 0 : 12,
              fontStyle: 'bold',
              fontFamily: 'sans-serif',
              fill: '#333',
              align: 'center',
              listening: false
            }"
        />
        <v-group
          v-for="(wall, index) in this.floorSchema?.lines"
          :key="'material-icon-' + wall.floor_param_id"
        >
          <v-circle
            v-if="wall?.has_material && getLineMidpoint(wall)"
            :config="{
              x: getLineMidpoint(wall).x,
              y: getLineMidpoint(wall).y,
              radius: 12,
              fill: 'rgba(0, 0, 0, .2)',
              stroke: 'rgba(255, 107, 53, .3)',
              strokeWidth: 2,
              listening: false
            }"
          />
          <v-image
            v-if="wall?.has_material && getLineMidpoint(wall)"
            :config="{
              image: this.materialIcon,
              x: getLineMidpoint(wall).x - 8,
              y: getLineMidpoint(wall).y - 8,
              width: 16,
              height: 16,
              listening: false,
              opacity: '.6'
            }"
          />
        </v-group>
      </v-layer>
    </v-stage>
    <q-card
      v-if="!!hoveredWall"
      class="tooltip"
      style="position: absolute; z-index: 9999"
      :style="this.getTooltipStyles()"
    >
      <div class="text-subtitle2 tw-p-2">
        {{ `${this.deletingMode ? 'Удалить - ' : ''}${hoveredWall?.floor_param_type_name} ${hoveredWall?.floor_param_type_name_relative ? `, ${hoveredWall?.floor_param_type_name_relative}` :''}` }}
        <div
          v-if="hoveredWall?.has_material"
          class="text-caption text-positive"
          style="margin-top: 4px;"
        >
          ✓ Есть материал
        </div>
      </div>
    </q-card>
  </div>
  <q-linear-progress v-else-if="this.schemaFetching" indeterminate />
  <NotFoundText v-else-if="!this.schemaFetching" />
</template>

<script>
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar';
import NotFoundText from "@/components/common/NotFoundText.vue";
import SchemaRevit from "./SchemaRevit.vue";
import { API_URL, MODAL_NAMES } from '@/constants';
import { deleteFloorMapLine, flattenPoints, onKonvaWheel, updateFloorParam } from './services';
import { useAppStore } from '@/services/store/app';
import { mapState } from 'pinia';

export default {
  name: "Schema",
  components: { NotFoundText, SchemaRevit },
  props: ['floorSchema', 'floorMapInfo', 'activeFloorParamTypeIds','updateParamBody','updateData','editingMode','getFloorSchemaData','activeData', 'bufferParams', 'getBufferParamsData', 'getFloorLegendData', 'updateFloorSchema', 'schemaFetching', 'activeFloorFlatId','flatBorders','params','floorMapId', 'deletingMode', 'editingMode', 'activeFlat', 'groupEditingMode'],
  data() {
    return {
      stageConfig: {
        draggable: true,
        scaleX: 0.2,
        scaleY: 0.2,
      },
      wrapperStyle: {
        position: 'relative',
      },
      tooltipX: 0,
      tooltipY: 0,
      backgroundImage: null,
      materialIcon: null,
      hoveredIndex: null,
      hoveredWall: null,
      activeObjectId: null,
      objectInfo: null,
      activeBufferObjectId: null,
      updatingParam: false,
      deleteLoading: false,
    };
  },
  computed: {
    ...mapState(useAppStore, ['menuDrawerOpen']),
    isRevitProject() {
      // Определяем, является ли проект Revit
      const resourceCode = this.floorMapInfo?.external_resource_code
      // Если это не AUTOCAD и есть external_resource_code, значит это Revit
      return resourceCode && resourceCode !== 'AUTOCAD'
    },
    stageView() {
      const view = {
        width: (window.innerWidth - 250) || 1170,
        height: (window.innerHeight - 205) || 700,
      }
      if(this.menuDrawerOpen)
        view.width = window.innerWidth || 1170
      return view
    },
    wrapperStyles() {
      const view = {
        width: `${(window.innerWidth - 250) || 1170}px`,
        height: `${(window.innerHeight - 205) || 700}px`,
      }
      if(this.menuDrawerOpen)
        view.width = `${window.innerWidth || 1170}px`
      return view
    },
  },
  methods: {
    onKonvaWheel,
    flattenPoints,
    getTooltipStyles() {
      if(this.menuDrawerOpen)
        return { left: this.tooltipX - 30 + 'px', top: this.tooltipY - 235 + 'px' }
      return { left: this.tooltipX - 280 + 'px', top: this.tooltipY - 235 + 'px' }
    },
    onMouseLeave(evt) {
      this.hoveredWall = null;
      this.hoveredIndex = null;
      const stage = evt.target.getStage();
      stage.container().style.cursor = 'default';
    },
    onMouseEnter(wall, evt, index) {
      this.hoveredIndex = index
      this.hoveredWall = wall;
      const pointerPos = evt.evt; // native MouseEvent
      this.tooltipX = pointerPos.clientX + 10;
      this.tooltipY = pointerPos.clientY + 10;

      const stage = evt.target.getStage();
      stage.container().style.cursor = 'pointer';
    },
    onUpdateObject() {
      this.getFloorLegendData()
      this.getFloorSchemaData(this.floorMapId)
      this.updateData && this.updateData()
    },
    showObjectInfoModal(wall) {
      if(!wall?.floor_param_id) {
        this.activeObjectId = null
        this.activeBufferObjectId = null
        return
      }
      this.showModal(MODAL_NAMES.FLOOR_SCHEMA_OBJECT_INFO, {
        title: `${wall?.floor_param_type_name || 'Информация об объекте'} (ID: ${wall?.floor_param_id})`,
        canChooseMaterial: this.floorMapInfo?.can_choose_material,
        floorMapId: this.floorMapId,
        data: wall, params: {floor_map_id: this.floorMapId, project_type_id: this.activeData.project_type_id}, resident_id: this.activeData?.resident_id,
        onClose: () => {
          this.activeObjectId = null
          this.activeBufferObjectId = null
        },
        onSubmit: this.onUpdateObject,
      })
    },
    loadBackground() {
      const src = this.floorSchema?.floor_map?.image_url
      if(!src) return
      const image = new Image();
      image.src = `${API_URL}${this.floorSchema?.floor_map?.image_url}`;
      image.onload = () => {
        this.backgroundImage = image;
      };
      image.onerror = () => {
        // Если не загрузилось с основного API, пробуем с запасного
        console.log('Основное изображение не загрузилось, пробуем запасной URL');
        const fallbackImage = new Image();
        fallbackImage.src = `https://master-api.smartremont.kz${this.floorSchema?.floor_map?.image_url}`;
        fallbackImage.onload = () => {
          this.backgroundImage = fallbackImage;
        };
        fallbackImage.onerror = () => {
          console.error('Не удалось загрузить изображение схемы этажа с обоих серверов');
        };
      };
    },
    loadMaterialIcon() {
      const image = new Image();
      image.src = '/img/icons/box.svg';
      image.onload = () => {
        this.materialIcon = image;
      };
    },
    getCircleSettings(room) {
      const {flat_num} = room
      const flatNumLength = String(flat_num).length
      let settings = {
        x: room.center_x + 50,
        y: room.center_y + 17,
        radius: 60,
        listening: false
      }
      if(flatNumLength === 1) {
        settings.x = room.center_x + 40
        settings.radius = 55
      } else if(flatNumLength === 3) {
        settings.x = room.center_x + 61
        settings.radius = 67
      }
      return {
        ...settings,
        fill: '#fff',
        opacity: '.7',
        stroke: '#ccc',
        strokeWidth: 1
      }
    },
    async updateFloorParamData(wall) {
      const { floor_param_id, floor_buffer_id } = wall
      const body = {...this.updateParamBody, floor_param_id, floor_buffer_id, floor_flat_id: this.activeFlat?.floor_flat_id,
        with_relative: !!this.groupEditingMode}

      this.updatingParam = true
      const res = await updateFloorParam(body, {floor_map_id: this.floorMapId})
      this.updatingParam = false
      if (!res) return
      this.getFloorLegendData()
      this.updateFloorSchema(res)
      this.showSucceedSnackbar()
      if(floor_buffer_id && this.bufferParams) {
        if(wall.coord_type === 'POINT') {
          this.bufferParams.circles = this.bufferParams?.circles?.filter(item => item.floor_buffer_id !== floor_buffer_id)
        } else {
          this.bufferParams.lines = this.bufferParams?.lines?.filter(item => item.floor_buffer_id !== floor_buffer_id)
        }
        if(this.groupEditingMode)
          return this.getBufferParamsData()
      }

    },
    async handleLineClick(wall) {
      if(!wall) return
      if(!this.editingMode) {
        if(wall.floor_param_id)
          this.activeObjectId = wall.floor_param_id
        else
          this.activeBufferObjectId = wall.floor_buffer_id
      }
      if(this.deletingMode) {
        this.deleteObject(wall)
        return
      }
      this.editingMode ? this.updateFloorParamData(wall) : this.showObjectInfoModal(wall)
    },
    async handlePointCommentClick(point) {
      this.showModal(MODAL_NAMES.SchemaPointInfo, {
        title: 'Информация о замечании',
        data: {...point, floorMapId: this.floorMapId},
      })
    },
    getLineWidth(wall) {
      let width = 8
      if(!this.editingMode) {
        if(this.activeFloorParamTypeIds?.length) {
          const isExist = this.activeFloorParamTypeIds.includes(wall?.floor_param_type_id)
          if(!isExist)
            width = 0
        }
        if(this.activeFloorFlatId) {
          if(wall?.floor_flat_id !== this.activeFloorFlatId)
            width = 0
        }
        if(this.updateParamBody?.room_id) {
          if(wall?.room_id !== this.updateParamBody?.room_id)
            width = 0
        }
      }
      return width
    },
    getLineDash(wall) {
      return wall?.floor_param_type_id_relative ? [10, 5] : []
    },
    getLineMidpoint(wall) {
      if (!wall.points || wall.points.length < 2) return null

      const points = this.flattenPoints(wall.points)

      const pointCount = points.length / 2 // Количество пар координат
      const midPointIndex = Math.floor(pointCount / 2)

      if (pointCount % 2 === 0) {
        const x1 = points[midPointIndex * 2 - 2]
        const y1 = points[midPointIndex * 2 - 1]
        const x2 = points[midPointIndex * 2]
        const y2 = points[midPointIndex * 2 + 1]

        return {
          x: (x1 + x2) / 2,
          y: (y1 + y2) / 2
        }
      } else {
        return {
          x: points[midPointIndex * 2],
          y: points[midPointIndex * 2 + 1]
        }
      }
    },
    async deleteObject(wall) {
      const { floor_param_id } = wall
      this.deleteLoading = true;
      const res = await deleteFloorMapLine(floor_param_id, {floor_map_id: this.floorMapId})
      this.deleteLoading = false;
      if(!res) return
      this.updateFloorSchema(res)
      this.getFloorLegendData()
      this.showSucceedSnackbar()
    },
    getLineColor(wall, index, floor_flat_id) {
      if(this.hoveredIndex === index) return 'blue'
      if(!wall.floor_param_id && this.activeBufferObjectId === wall.floor_buffer_id) return '#970e0e'
      if(!wall.floor_param_id) return '#000'
      if(this.activeObjectId === wall.floor_param_id && !!wall.floor_param_id) return '#970e0e'
      if(this.flatBorders && this.floorSchema?.flatColors) return this.floorSchema?.flatColors[floor_flat_id]

      return wall?.floor_param_type_color || '#000'
    },
    onWheel(e) {
      const stage = this.$refs.stage.getNode();
      this.onKonvaWheel(e, stage)
    },
  },
  watch: {
    floorSchema: function () {
      this.loadBackground()
    },
  },
  mounted() {
    this.loadBackground();
    this.loadMaterialIcon();
  },
  setup() {
    const {showModal} = useAppStore()
    const {showSucceedSnackbar} = useCustomSnackbar()
    return {showSucceedSnackbar, showModal}
  }
};
</script>
